import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { API } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell,
} from "recharts";
import { Users, Boxes, TrendingUp, UserCheck, Target, Trophy, Settings2, Cake, Gift, ListTodo, AlertTriangle, Clock } from "lucide-react";
import { waLink } from "@/lib/modules";
import { WeeklyEntry } from "@/components/WeeklyEntry";
import { QuickActions } from "@/components/QuickActions";
import { FirstSteps } from "@/components/FirstSteps";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const fmtDate = (s) => {
  try {
    const d = new Date(s + "T00:00:00");
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  } catch { return s; }
};

function Kpi({ icon: Icon, label, value, suffix, testid, tone = "primary" }) {
  const tones = {
    primary: "text-primary bg-primary/10",
    gold: "text-accent bg-accent/10",
    emerald: "text-[hsl(var(--emerald))] bg-[hsl(var(--emerald))]/10",
    sky: "text-[hsl(var(--sky))] bg-[hsl(var(--sky))]/10",
  };
  return (
    <Card className="p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</span>
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </span>
      </div>
      <div className="mt-4 font-display text-4xl font-bold">
        {value}
        {suffix && <span className="text-xl text-muted-foreground ml-1">{suffix}</span>}
      </div>
    </Card>
  );
}

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-sm shadow-lg">
      <div className="font-medium">{fmtDate(label)}</div>
      <div className="text-primary">{payload[0].value}% presença</div>
    </div>
  );
};

function GoalDialog({ goal, onSaved }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(goal);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    if (open) api.get("/settings").then((r) => { setValue(r.data.attendance_goal); setMsg(r.data.birthday_message || ""); });
  }, [open]);
  const save = async () => {
    try {
      await api.put("/settings", { attendance_goal: value, birthday_message: msg });
      toast.success(`Configurações salvas`);
      setOpen(false);
      onSaved();
    } catch {
      toast.error("Erro ao salvar");
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="open-goal-settings-button">
          <Settings2 className="h-4 w-4" /> Configurações
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Configurações gerais</DialogTitle></DialogHeader>
        <p className="text-sm font-medium">Meta global de frequência</p>
        <p className="text-xs text-muted-foreground">Aplicada no painel, na celebração e como padrão dos certificados.</p>
        <div className="flex items-center gap-4 py-2">
          <Slider value={[value]} onValueChange={(v) => setValue(v[0])} min={50} max={100} step={5} data-testid="goal-slider" />
          <Badge className="bg-accent/15 text-accent border-none text-base px-3 py-1 shrink-0">{value}%</Badge>
        </div>
        <div className="pt-2">
          <p className="text-sm font-medium">Mensagem de felicitação de aniversário</p>
          <p className="text-xs text-muted-foreground mb-1.5">Use <code>{"{nome}"}</code> para inserir o primeiro nome.</p>
          <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} data-testid="birthday-message-input" />
        </div>
        <DialogFooter><Button onClick={save} data-testid="save-goal-button">Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [bdays, setBdays] = useState([]);
  const [bdayMsg, setBdayMsg] = useState("");
  const [progress, setProgress] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [commitSummary, setCommitSummary] = useState(null);

  const load = () => {
    const isAdmin = !!user?.is_admin || user?.role === "coordenador";
    api.get("/dashboard", { params: isAdmin ? {} : { mine: true } }).then((r) => setData(r.data)).catch(() => {});
    api.get("/ranking").then((r) => setRanking(r.data)).catch(() => {});
    api.get("/birthdays", { params: { month: new Date().getMonth() + 1 } }).then((r) => setBdays(r.data)).catch(() => {});
    api.get("/settings").then((r) => setBdayMsg(r.data.birthday_message || "")).catch(() => {});
    api.get("/learn/my-progress").then((r) => setProgress(r.data)).catch(() => {});
    if ((user?.allowed_modules || []).includes("metas") || isAdmin)
      api.get("/commitments/summary").then((r) => setCommitSummary(r.data)).catch(() => {});
    if (isAdmin) api.get("/users/pending").then((r) => setPendingCount(r.data.length)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const isMyBirthday = (() => {
    const bd = user?.birth_date;
    if (!bd) return false;
    const t = new Date();
    const d = new Date(bd + "T00:00:00");
    return d.getDate() === t.getDate() && d.getMonth() === t.getMonth();
  })();

  if (!data) return <div className="text-muted-foreground">Carregando indicadores…</div>;

  const goal = data.goal || 75;
  const trend = data.trend.map((t) => ({ ...t, label: t.date }));
  const goalMet = data.avg_attendance_rate >= goal;
  const medals = ["text-[hsl(var(--gold))]", "text-slate-300", "text-amber-700"];
  const todayDay = new Date().getDate();
  const upcoming = bdays.filter((b) => b.day >= todayDay).slice(0, 5);
  const greet = (p) => {
    const first = p.name.split(" ")[0];
    const text = (bdayMsg || "🎉 Feliz aniversário, {nome}!").replace(/\{nome\}/g, first);
    const link = waLink(p.phone, text);
    if (!link) return toast.error("Sem telefone cadastrado.");
    window.open(link, "_blank");
  };

  const isAdmin = !!user?.is_admin || user?.role === "coordenador";
  const firstName = (() => {
    const parts = (user?.name || "").trim().split(/\s+/).filter(Boolean);
    const honorifics = ["pr", "pr.", "pra", "pra.", "pastor", "pastora", "rev", "rev.", "diác", "diác.", "diácono", "dr", "dr.", "dra", "dra."];
    const first = parts.find((p) => !honorifics.includes(p.toLowerCase()));
    return first || parts[0] || "";
  })();
  const attention = [];
  if (isAdmin && pendingCount > 0) attention.push({ icon: UserCheck, text: `${pendingCount} cadastro${pendingCount > 1 ? "s" : ""} aguardando aprovação` });
  if (commitSummary && commitSummary.atrasados > 0) attention.push({ icon: AlertTriangle, text: `${commitSummary.atrasados} compromisso${commitSummary.atrasados > 1 ? "s" : ""} atrasado${commitSummary.atrasados > 1 ? "s" : ""}` });
  if (upcoming.length > 0) attention.push({ icon: Cake, text: `${upcoming.length} aniversariante${upcoming.length > 1 ? "s" : ""} este mês` });
  if (!goalMet) attention.push({ icon: TrendingUp, text: `Frequência ${data.avg_attendance_rate}% (meta ${goal}%)` });

  return (
    <div className="space-y-8 animate-fade-up" data-testid="dashboard-page">
      <Card className="p-6 bg-gradient-to-br from-primary/10 to-transparent" data-testid="dashboard-greeting">
        <h2 className="font-display text-2xl font-bold">Olá, {firstName} 👋</h2>
        {attention.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {attention.map((a, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-background/70 border border-border px-3 py-1.5 text-sm" data-testid="attention-chip">
                <a.icon className="h-4 w-4 text-primary" /> {a.text}
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="attention-allclear">Tudo em dia por aqui. Bom trabalho! 🙌</p>
        )}
      </Card>
      {isMyBirthday && (
        <Card className="p-6 bg-gradient-to-r from-accent/20 to-primary/15 border-accent/40 flex items-center gap-4" data-testid="my-birthday-banner">
          <span className="text-4xl">🎉</span>
          <div>
            <h3 className="font-display text-xl font-bold">Feliz aniversário, {user?.name?.split(" ")[0]}! 🎂</h3>
            <p className="text-sm text-muted-foreground">Toda a família Aprisco Church — Zona Norte celebra com você hoje. Que Deus te abençoe!</p>
          </div>
        </Card>
      )}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Painel de Indicadores</h1>
          <p className="text-muted-foreground mt-1">Visão geral dos Pequenos Grupos de Discipulado.</p>
        </div>
        {user?.role === "coordenador" && <GoalDialog goal={goal} onSaved={load} />}
      </div>

      <QuickActions />
      <FirstSteps />

      <WeeklyEntry variant="dashboard" isAdmin={!!user?.is_admin || user?.role === "coordenador"} />

      {commitSummary && (commitSummary.minhas > 0 || commitSummary.pendentes > 0 || commitSummary.atrasados > 0) && (
        <Card className="p-6" data-testid="dashboard-commitments-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <ListTodo className="h-5 w-5 text-primary" /> Metas & Compromissos
            </h3>
            <Button variant="outline" size="sm" onClick={() => navigate("/metas")} data-testid="goto-metas-button">Abrir</Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <button onClick={() => navigate("/metas")} className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-left hover:bg-primary/10 transition-colors" data-testid="dash-commit-minhas">
              <div className="text-2xl font-bold text-primary leading-none">{commitSummary.minhas}</div>
              <div className="text-xs text-muted-foreground mt-1">Minhas em aberto</div>
            </button>
            <button onClick={() => navigate("/metas")} className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left hover:bg-amber-100 transition-colors" data-testid="dash-commit-pendentes">
              <div className="text-2xl font-bold text-amber-700 leading-none">{commitSummary.pendentes}</div>
              <div className="text-xs text-muted-foreground mt-1">Pendentes</div>
            </button>
            <button onClick={() => navigate("/metas")} className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-left hover:bg-red-100 transition-colors" data-testid="dash-commit-atrasados">
              <div className="text-2xl font-bold text-red-700 leading-none">{commitSummary.atrasados}</div>
              <div className="text-xs text-muted-foreground mt-1">Atrasados</div>
            </button>
            <button onClick={() => navigate("/metas")} className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-left hover:bg-sky-100 transition-colors" data-testid="dash-commit-vencendo">
              <div className="text-2xl font-bold text-sky-700 leading-none">{commitSummary.vencendo}</div>
              <div className="text-xs text-muted-foreground mt-1">Vencendo (7 dias)</div>
            </button>
          </div>
        </Card>
      )}

      {progress && (progress.estudo.total > 0 || progress.escola.total > 0) && (
        <Card className="p-6" data-testid="my-formation-card">
          <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" /> Minha Formação
          </h3>
          <div className="grid sm:grid-cols-2 gap-6">
            {[["Estudos do Mês", progress.estudo], ["Escola de Liderança", progress.escola]].map(([label, p]) => {
              const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;
              return (
                <div key={label} data-testid={`formation-${label.includes("Estudos") ? "estudo" : "escola"}`}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="font-medium">{label}</span>
                    <span className="text-muted-foreground">{p.done}/{p.total} · {pct}%</span>
                  </div>
                  <Progress value={pct} />
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Kpi icon={Boxes} label="PGDs Ativos" value={data.active_groups} testid="dashboard-kpi-active-groups" tone="primary" />
        <Kpi icon={Users} label="Membros" value={data.total_members} testid="dashboard-kpi-total-members" tone="gold" />
        <Kpi icon={TrendingUp} label="Frequência Média" value={data.avg_attendance_rate} suffix="%" testid="dashboard-kpi-attendance-rate" tone="emerald" />
        <Kpi icon={UserCheck} label="Líderes Formados" value={data.leaders} testid="dashboard-kpi-leaders" tone="sky" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2" data-testid="dashboard-chart-attendance-trend">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-display text-xl font-semibold">Tendência de Frequência</h3>
              <p className="text-sm text-muted-foreground">Últimas semanas de encontros</p>
            </div>
            <Badge className={goalMet ? "bg-[hsl(var(--emerald))]/15 text-[hsl(var(--emerald))] border-[hsl(var(--emerald))]/30" : "bg-destructive/15 text-destructive"}>
              <Target className="h-3.5 w-3.5 mr-1" /> Meta {goal}%
            </Badge>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trend} margin={{ left: -20, right: 8 }}>
              <defs>
                <linearGradient id="attGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(21 90% 48%)" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="hsl(21 90% 48%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" tickFormatter={fmtDate} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="rate" stroke="hsl(21 90% 48%)" strokeWidth={2.5} fill="url(#attGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6" data-testid="dashboard-group-rates">
          <h3 className="font-display text-xl font-semibold mb-6">Frequência por Grupo</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.group_rates} layout="vertical" margin={{ left: 10, right: 16 }}>
              <XAxis type="number" domain={[0, 100]} hide />
              <YAxis type="category" dataKey="name" width={110} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Bar dataKey="rate" radius={[0, 6, 6, 0]}>
                {data.group_rates.map((g, i) => (
                  <Cell key={i} fill={g.rate >= goal ? "hsl(var(--emerald))" : "hsl(21 90% 48%)"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6" data-testid="dashboard-birthdays">
          <h3 className="font-display text-xl font-semibold mb-4 flex items-center gap-2"><Cake className="h-5 w-5 text-accent" /> Aniversariantes do mês</h3>
          <div className="space-y-2">
            {upcoming.map((b) => (
              <div key={b.id} className={`flex items-center gap-3 rounded-lg border px-4 py-2.5 ${b.is_today ? "border-accent/40 bg-accent/5" : "border-border"}`} data-testid="dashboard-birthday-row">
                <span className="font-mono text-sm w-12 text-muted-foreground">{String(b.day).padStart(2, "0")}/{String(b.month).padStart(2, "0")}</span>
                <span className="flex-1 min-w-0 truncate font-medium">{b.name}{b.is_today && <span className="text-accent text-xs ml-2">🎉 hoje!</span>}</span>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => greet(b)} data-testid={`dashboard-greet-${b.id}`}>
                  <Gift className="h-3.5 w-3.5" /> Felicitar
                </Button>
              </div>
            ))}
            {upcoming.length === 0 && <p className="text-muted-foreground text-sm">Nenhum aniversariante restante neste mês.</p>}
          </div>
        </Card>

        <Card className="p-6" data-testid="dashboard-ranking">
          <h3 className="font-display text-xl font-semibold mb-5 flex items-center gap-2"><Trophy className="h-5 w-5 text-[hsl(var(--gold))]" /> Ranking dos Grupos</h3>
          <p className="text-xs text-muted-foreground mb-4">Pontuação combinando frequência (70%) e satisfação (30%).</p>
          <div className="space-y-2">
            {ranking.map((g, i) => (
              <div key={g.group_id} className="flex items-center gap-3 rounded-lg border border-border px-4 py-3" data-testid="ranking-row">
                <span className={`font-display text-lg font-bold w-6 ${medals[i] || "text-muted-foreground"}`}>{i + 1}º</span>
                {g.leader_avatar ? (
                  <img src={`${API}/user-photo/${g.leader_avatar}`} alt="" className="h-8 w-8 rounded-full object-cover" />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-xs font-bold">{g.leader?.charAt(0) || "?"}</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{g.name}</div>
                  <div className="text-xs text-muted-foreground">{g.leader} · {g.rate}% freq · {g.satisfaction || "—"}/5 sat.</div>
                </div>
                <Badge className="bg-primary/15 text-primary border-none shrink-0">{g.score} pts</Badge>
              </div>
            ))}
            {ranking.length === 0 && <p className="text-muted-foreground text-sm">Sem dados de ranking ainda.</p>}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card className="p-6">
          <h3 className="font-display text-xl font-semibold mb-4">Grupos que atingiram a meta (≥ {goal}%)</h3>
          <div className="flex flex-wrap gap-3">
            {data.above_75.length === 0 && <p className="text-muted-foreground text-sm">Nenhum grupo atingiu a meta ainda nesta janela.</p>}
            {data.above_75.map((g) => (
              <div key={g.name} className="flex items-center gap-3 rounded-lg border border-[hsl(var(--emerald))]/30 bg-[hsl(var(--emerald))]/5 px-4 py-3">
                <span className="font-medium">{g.name}</span>
                <Badge className="bg-[hsl(var(--emerald))]/15 text-[hsl(var(--emerald))] border-none">{g.rate}%</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
